import { Icon, Text } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import type * as React from "react";

import { DROPDOWN_MENU_PARTS, type MenuParts } from "./menu-parts";
import { copyText, openHref } from "#app/lib/utils/copy-actions";

/**
 * The spine every linkable entity's menu shares — the project roster
 * (`renderIssueMenuItems`) with its project-only property submenus removed:
 *
 *   Favorite / Unfavorite
 *   ─────────
 *   Open in new tab · Copy link · [the host's extras]
 *   ─────────
 *   Delete (destructive)
 *
 * Accounts, Contacts and dashboards render it from every surface they carry a
 * menu on — the directory table's kebab and row right-click, and the sidebar
 * favorite's ⋯ and right-click — so a favorite offers the same actions, in the
 * same order and dress, as the entity anywhere else. The host passes the
 * menu-kind parts (`CONTEXT_MENU_PARTS` for a right-click) exactly as the
 * project and workflow rosters take them.
 */
export type BasicEntityMenuItemsProps = Omittable<{
  /** Menu part set of the hosting menu kind. Defaults to the dropdown parts. */
  parts?: MenuParts;
  /** The entity's shareable URL — what Open in new tab opens and Copy link
   *  copies. */
  href: string;
  isFavorited: boolean;
  onFavorite: () => void;
  /** Host-specific rows seated after Copy link, inside the link group. */
  extra?: React.ReactNode;
  /** Omitted when the viewer cannot delete — the row is absent, never
   *  disabled, because the flat permission map cannot state a conditional
   *  (`if_created`) verdict as a reason. */
  onDelete?: () => void;
}>;

export function renderBasicEntityMenuItems({
  parts = DROPDOWN_MENU_PARTS,
  href,
  isFavorited,
  onFavorite,
  extra,
  onDelete,
}: BasicEntityMenuItemsProps) {
  const { Item, Separator } = parts;

  return (
    <>
      <Item onClick={onFavorite}>
        <Icon
          color="muted"
          name={isFavorited ? "star-filled" : "star"}
          size="md"
        />
        <Text size="sm">{isFavorited ? "Unfavorite" : "Favorite"}</Text>
      </Item>
      <Separator />
      <Item onClick={() => openHref(href)}>
        <Icon color="muted" name="external-link" size="md" />
        <Text size="sm">Open in new tab</Text>
      </Item>
      <Item onClick={() => void copyText(href, "Link")}>
        <Icon color="muted" name="link" size="md" />
        <Text size="sm">Copy link</Text>
      </Item>
      {extra}
      {onDelete ? (
        <>
          <Separator />
          <Item onClick={onDelete} variant="destructive">
            <Icon name="trash" size="md" />
            <Text size="sm">Delete</Text>
          </Item>
        </>
      ) : null}
    </>
  );
}
